import type { CampaignsReportResponse, ReportPerson, SalesReportResponse } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useState, type ReactNode } from "react";
import { ApiRequestError } from "../api/http";
import { useTab } from "../api/queries";
import { Segmented } from "../design/components/Segmented";
import { localDayOf } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import { settingsSectionsOf } from "../routes/settings/sections";
import { reportsScopeOf, useAccess, useMenuSubject } from "../session/access";
import { commercialViewOf } from "../session/tab-context";
import { CampaignsTab } from "./CampaignsTab";
import { DaysSection } from "./DaysSection";
import { GoalsSection } from "./GoalsSection";
import { HeatSection } from "./HeatSection";
import type { KpiTableGroup } from "./KpiTable";
import { ReportControls } from "./ReportControls";
import { compareText, isEmptyCampaigns, isEmptyReport, rangeText, sentenceStart } from "./report-format";
import { campaignsReportQuery, salesReportQuery } from "./report-queries";
import { chosenOf, DEFAULT_REPORT_PERIOD, expectedRangesOf, reportsQueryOf, validateReportsSearch, type ReportsSearch } from "./report-search";
import { ReportsEmpty, ReportsError, ReportsLoading, ReportsNoAccess } from "./ReportStates";
import { SalesSummary } from "./SalesSummary";
import { TeamSection } from "./TeamSection";
import { TrendSection, type TrendMetric } from "./TrendSection";
import { WorkBlocksSection } from "./WorkBlocksSection";
import "../design/components/page-header.css";
import "./reports.css";

// La pagina «Report» (piano M7, Task 3 e 5; D11; mock-up `report.html` del 17/10/2026 e la sua nota; SEZ-1, SEZ-2, SEZ-3, SEZ-16): in
// testata «Report», «I numeri del lavoro» e le due schede, «Commerciali» e «Campagne» (`vista=campagne`); sotto il periodo, «Di chi» e il
// confronto (`ReportControls.tsx`); poi i quattro gruppi del foglio con frecce e obiettivi (`SalesSummary.tsx`), «Una riga per
// Commerciale» per chi vede il team (`TeamSection.tsx`), «Andamento» (`TrendSection.tsx`), «Giorno per giorno» (`DaysSection.tsx`) e
// «Quando si chiama» (`HeatSection.tsx`), nell'ordine del mock-up: «Obiettivi del mese» (`GoalsSection.tsx`, Task 6) dopo il team (per
// il Commerciale dopo il sommario), «La giornata a blocchi di 30 minuti» (`WorkBlocksSection.tsx`, Task 7) in fondo. «Campagne» ha «Le campagne» e «Vendite per campagne» (`CampaignsTab.tsx`). Scheda, periodo e persone stanno nell'indirizzo
// (`report-search.ts`); i numeri sono una lettura di `GET /api/reports/sales` o di `GET /api/reports/campaigns` (`report-queries.ts`),
// soltanto quella della scheda aperta, che il browser trasforma con le formule del dominio. Cambiando scheda periodo e persone restano.
//
// Chi vede che cosa lo decide il server (SEZ-1): il Commerciale soltanto i suoi numeri, senza colleghi né totali del team, e la pagina
// non chiede nemmeno «Di chi»; chi vede il team sceglie le persone. Nella «Guarda come…» il Report è quello della persona osservata
// (RUOLI-18): l'ambito è il suo, e la richiesta porta la vista. Senza l'area dei report la pagina non legge niente e dice perché.
// Mentre una lettura nuova arriva, periodo e persone restano dove sono (le persone della lettura di prima) e i numeri lasciano il posto
// al caricamento: mai i numeri di un periodo sotto le parole di un altro.

/** Lo stesso indirizzo senza i valori tolti: un periodo tornato al «Mese» o «Tutto il team» non restano scritti. */
function compact(search: ReportsSearch): ReportsSearch {
  return Object.fromEntries(Object.entries(search).filter(([, value]) => value !== undefined)) as ReportsSearch;
}

/** Le due letture hanno la stessa testa (periodi, ambito, persone): basta per le frasi e per «Di chi». */
type ReportHead = Pick<SalesReportResponse, "range" | "previous" | "scope" | "people" | "chosen">;

const isCampaigns = (report: SalesReportResponse | CampaignsReportResponse): report is CampaignsReportResponse => "campaigns" in report;

/** Il titolo del sommario: «I numeri del team», «I numeri di {nome}», «I numeri delle {n} persone scelte» o «I tuoi numeri». */
function summaryTitle(t: Translate, team: boolean, chosen: readonly string[], people: readonly ReportPerson[]): string {
  if (!team) return t("reports.summary.own");
  if (chosen.length === 0) return t("reports.summary.team");
  if (chosen.length === 1) return t("reports.summary.person", { name: people.find((person) => person.membershipId === chosen[0])?.displayName ?? chosen[0] ?? "" });
  return t("reports.summary.people", { count: chosen.length });
}

export function ReportsPage() {
  const t = useT();
  const { locale } = useLocale();
  const tab = useTab();
  const access = useAccess();
  // L'ambito del menu della scheda: nella «Guarda come…» quello della persona osservata, come sul server.
  const subject = useMenuSubject();
  // Di nuovo dalla forma, come «Conversazioni»: un valore scartato resta grezzo nella ricerca della rotta e non deve arrivare al server.
  const search = validateReportsSearch(useSearch({ from: "/autenticato/guscio/azienda/report" }));
  const navigate = useNavigate({ from: "/report" });
  const scope = subject.access === null ? "none" : reportsScopeOf(subject.access);
  // «Di chi» è soltanto di chi vede il team: per gli altri non arriva al server (che lo ignorerebbe).
  const query = reportsQueryOf(scope === "all" ? search : { ...search, chi: undefined });
  const view = commercialViewOf(tab);
  const campaignsTab = search.vista === "campagne";
  // La lettura di prima resta mentre arriva la nuova soltanto nella stessa vista: cambiando la persona di «Guarda come…» il nome e le
  // persone di prima non restano sotto la vista nuova. Si legge soltanto la scheda aperta.
  const salesReport = useQuery({
    ...salesReportQuery(tab, query),
    enabled: scope !== "none" && !campaignsTab,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[4] === view ? previous : undefined),
  });
  const campaignsReport = useQuery({
    ...campaignsReportQuery(tab, query),
    enabled: scope !== "none" && campaignsTab,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[4] === view ? previous : undefined),
  });
  const report = campaignsTab ? campaignsReport : salesReport;
  // Le persone dell'altra scheda, già lette per la stessa query, mentre arriva la prima risposta di questa.
  const other: ReportHead | undefined = campaignsTab ? salesReport.data : campaignsReport.data;
  const timeZone = access.context.settings.timeZone;
  // Il gruppo di colonne delle due tabelle resta nello stato della pagina (D11; `state.teamGroup` e `state.dayGroup` del mock-up):
  // sopravvive a un periodo o a «Di chi» cambiati. Il team parte da «Sintesi», «Giorno per giorno» da «Chiamate», come nel mock-up.
  const [teamGroup, setTeamGroup] = useState<KpiTableGroup>("summary");
  const [dayGroup, setDayGroup] = useState<KpiTableGroup>("calls");
  // La metrica dell'andamento, come i gruppi di colonne (`state.trend` del mock-up): parte da «Chiamate».
  const [trendMetric, setTrendMetric] = useState<TrendMetric>("calls");
  // La persona e il giorno della giornata a blocchi, come i gruppi di colonne (`state.blockPerson` e `state.blockDay` del mock-up).
  const [blockPerson, setBlockPerson] = useState<string | null>(null);
  const [blockDay, setBlockDay] = useState<string | null>(null);

  // Periodo e persone vanno nella cronologia: «Indietro» del browser riporta la vista di prima.
  const update = useCallback(
    (change: Partial<ReportsSearch>) => void navigate({ search: (previous) => compact({ ...validateReportsSearch(previous), ...change }) }),
    [navigate],
  );

  const refused = report.error instanceof ApiRequestError && report.error.code === "permission_denied";
  const tabLabel = campaignsTab ? t("reports.tabs.campaigns") : t("reports.tabs.sales");
  if (scope === "none" || refused) return <ReportsNoAccess />;

  const now = new Date().toISOString();
  const today = localDayOf(timeZone, now);
  // Le persone e l'ambito restano quelli dell'ultima risposta mentre arriva la nuova, se è dello stesso ambito (entrando o uscendo da
  // «Guarda come…» no); i numeri e i giorni soltanto da quella giusta.
  const data: SalesReportResponse | CampaignsReportResponse | undefined = report.data;
  const shown = data !== undefined && (!report.isPlaceholderData || data.scope === scope) ? data : null;
  const fresh = shown !== null && !report.isPlaceholderData ? shown : null;
  const head: ReportHead | null = shown ?? (other !== undefined && other.scope === scope ? other : null);
  const team = (head?.scope ?? scope) === "all";
  const chosen = team ? chosenOf(search) : [];
  const people = head?.people ?? [];
  const ranges = fresh ?? expectedRangesOf(search, today, now);
  const range = rangeText(t, timeZone, ranges.range);
  const title = summaryTitle(t, team, chosen, people);
  const watching = view !== null;
  const toMonth = () => update({ periodo: undefined, dal: undefined, al: undefined });
  const toTeam = () => update({ chi: undefined });
  const onMonth = (search.periodo ?? DEFAULT_REPORT_PERIOD) === DEFAULT_REPORT_PERIOD ? null : toMonth;
  const names = people.filter((person) => chosen.includes(person.membershipId)).map((person) => person.displayName);
  // «Imposta gli obiettivi» soltanto a chi ha la sezione nel suo menu (nella «Guarda come…» quello della persona osservata).
  const canSetGoals = settingsSectionsOf(subject).some((section) => section.id === "goals");

  // L'interruttore è soltanto del Super admin di Gioia365 nel contesto di piattaforma, fuori dalla «Guarda come…».
  const switchable = access.isPlatform && !watching ? { company: access.context.organization.name } : null;
  // Le due sezioni che non dipendono dal periodo, nel Report con i numeri e in quello vuoto.
  const goalsOf = (report: SalesReportResponse) => (
    <GoalsSection
      goals={report.goals}
      targets={report.targets}
      whose={!team ? { kind: "own" } : chosen.length > 0 ? { kind: "chosen", names } : { kind: "team" }}
      canSet={canSetGoals}
    />
  );
  const blocksOf = (report: SalesReportResponse) => (
    <WorkBlocksSection
      report={report}
      timeZone={timeZone}
      person={blockPerson}
      day={blockDay}
      onPersonChange={setBlockPerson}
      onDayChange={setBlockDay}
      switchable={switchable}
    />
  );

  let results: ReactNode;
  if (report.isError) {
    results = <ReportsError error={report.error} onRetry={() => void report.refetch()} onMonth={toMonth} onTeam={chosen.length > 0 ? toTeam : null} />;
  } else if (fresh === null) {
    results = campaignsTab ? <ReportsLoading title={t("reports.campaigns.title")} groups={false} /> : <ReportsLoading title={title} />;
  } else if (isCampaigns(fresh)) {
    if (isEmptyCampaigns(fresh)) {
      results = <ReportsEmpty kind="campaigns" range={sentenceStart(locale, range)} chosen={chosen.length > 0} onMonth={onMonth} onTeam={toTeam} />;
    } else {
      const whose = !team ? t("reports.campaigns.whoseOwn") : chosen.length > 0 ? t("reports.campaigns.whoseChosen", { names: names.join(", ") }) : t("reports.campaigns.whoseTeam");
      results = <CampaignsTab report={fresh} range={range} whose={whose} timeZone={timeZone} />;
    }
  } else if (isEmptyReport(fresh.totals.current)) {
    // «Obiettivi del mese» e l'interruttore della giornata a blocchi non dipendono dal periodo: restano anche senza numeri. La giornata a
    // blocchi spenta e senza interruttore no (nel mock-up lo stato vuoto non mostra altro); accesa non ha giorni da mostrare, a Gioia365
    // resta «Spegni per {azienda}».
    results = (
      <>
        <ReportsEmpty range={sentenceStart(locale, range)} chosen={chosen.length > 0} onMonth={onMonth} onTeam={toTeam} />
        {goalsOf(fresh)}
        {fresh.workBlocks.enabled || switchable !== null ? blocksOf(fresh) : null}
      </>
    );
  } else {
    const whose = !team ? (people[0]?.displayName ?? "") : chosen.length > 0 ? names.join(", ") : t("reports.controls.wholeTeam");
    results = (
      <>
        <SalesSummary title={title} current={fresh.totals.current} previous={fresh.totals.previous} targets={fresh.targets} />
        {fresh.byPerson !== null ? <TeamSection report={fresh} range={range} group={teamGroup} onGroupChange={setTeamGroup} /> : null}
        {goalsOf(fresh)}
        <TrendSection report={fresh} range={range} metric={trendMetric} onMetricChange={setTrendMetric} />
        <DaysSection report={fresh} range={range} whose={whose} group={dayGroup} onGroupChange={setDayGroup} />
        <HeatSection report={fresh} />
        {blocksOf(fresh)}
      </>
    );
  }

  return (
    <div className="g-report">
      <div className="g-page-head g-report__head">
        <div>
          <p className="g-page-head__eyebrow">{t("shell.nav.reports")}</p>
          <h1 className="g-page-head__title">{t("reports.title")}</h1>
          <p className="g-report__intro">{campaignsTab ? t("reports.introCampaigns") : t("reports.intro")}</p>
        </div>
        <Segmented
          label={t("reports.tabsLabel")}
          options={[
            { value: "sales", label: t("reports.tabs.sales") },
            { value: "campaigns", label: t("reports.tabs.campaigns") },
          ]}
          value={campaignsTab ? "campaigns" : "sales"}
          onChange={(value) => {
            // La scheda va nella cronologia come periodo e persone; la stessa scheda non aggiunge niente.
            if ((value === "campaigns") !== campaignsTab) update({ vista: value === "campaigns" ? "campagne" : undefined });
          }}
        />
      </div>
      <ReportControls
        search={search}
        update={update}
        today={today}
        rangeLabel={sentenceStart(locale, range)}
        compareLabel={compareText(t, timeZone, ranges.previous)}
        people={head === null ? null : { scope: head.scope, list: head.people }}
        watching={watching}
      />
      <p className="g-visually-hidden" role="status">
        {t("reports.status", { tab: tabLabel, range })}
      </p>
      <div className="g-report__results" aria-busy={fresh === null && !report.isError ? true : undefined}>
        {results}
      </div>
    </div>
  );
}
